import { CommonModule, CurrencyPipe } from '@angular/common';
import { Component, Input, OnChanges, OnInit, signal, SimpleChanges } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { ApexOptions, NgApexchartsModule } from 'ng-apexcharts';
import { ContractDto, SalesYearlySummary } from '../mandat-dashboard/mandat-dashboard.service';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-total-revenue',
  templateUrl: './total-revenue.component.html',
  styleUrls: ['./total-revenue.component.css'],
  imports: [MatIconModule, NgApexchartsModule, MatMenuModule, MatButtonModule, MatButtonToggleModule, CurrencyPipe, CommonModule, TranslocoPipe]
})

export class TotalRevenueComponent implements OnChanges {
  chartCompleted: ApexOptions; //VISITS
  chartDelivered: ApexOptions; //Impressions
  chartAccounted: ApexOptions; //Conversions
  chartSummary: ApexOptions;
  @Input() data: SalesYearlySummary;
  @Input() year = signal<number>(new Date().getFullYear());
  constructor(private transloco: TranslocoService) { }

  ngOnChanges(changes: SimpleChanges) {
    this._initializeData();
  }

  get getCompletedByMonth() {
    return this._groupContractsByMonth('completed');
  }

  get getDeliveredByMonth() {
    return this._groupContractsByMonth('delivered');
  }

  get getAccountedByMonth() {
    return this._groupContractsByMonth('accounted');
  }

  setYear(value: number | string) {
    if (value === 'last-year') {
      this.year.set(new Date().getFullYear() - 1);
    }
    else if (value === 'this-year') {
      this.year.set(new Date().getFullYear());
    }
  }

  calcTrend(current: number, previous: number) {
    // both zero → nothing changed
    if (current === 0 && previous === 0) {
      return { percent: 0, up: false };
    }

    // went from 0 → some value
    if (previous === 0 && current > 0) {
      return { percent: 100, up: true };
    }

    // went from some value → 0
    if (current === 0 && previous > 0) {
      return { percent: 0, up: false };
    }

    // normal case
    const diff = current - previous;
    const pct = Math.round((diff / previous) * 100);

    return {
      percent: Math.abs(pct),
      up: diff >= 0
    };
  }


  // Example getters (use wherever you render the KPI)
  get completedTrend() {
    return this.calcTrend(this.data.totalCompleted, this.data.previousYearCompleted);
  }
  get deliveredTrend() {
    return this.calcTrend(this.data.totalDelivered, this.data.previousYearDelivered);
  }
  get accountedTrend() {
    return this.calcTrend(this.data.totalAccounted, this.data.previousYearAccounted);
  }

  private _groupContractsByMonth(status: string) {
    if (!this.data?.contracts) return [{ name: status, data: [] }];

    // Months Jan → Dec
    const months = Array.from({ length: 12 }, (_, i) => ({
      month: i,
      total: 0
    }));

    this.data.contracts
      .filter(c => c.status.toLowerCase() === status.toLowerCase())
      .forEach(c => {
        const date = new Date(c.date);
        if (date.getFullYear() === new Date().getFullYear()) {
          months[date.getMonth()].total += 1;
        }
      });

    var e = [
      {
        name: status.charAt(0).toUpperCase() + status.slice(1),
        data: months.map(m => m.total)
      }
    ];
    return e;
  }

  get months() {
    return [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
  }


  buildWeeklyRevenueExpenseSeriesCurrentYear(
    contracts: ContractDto[]
  ) {
    if (!contracts?.length) {
      return [
        { name: 'Revenue', data: [] as { x: Date; y: string }[] },
        { name: 'Expenses', data: [] as { x: Date; y: string }[] },
      ];
    }

    const year = this.year();
    const weekStarts = this._allISOWeekStartsOfYear(year);

    // zero-initialize all weeks for smooth line
    const rev = new Map<number, number>();
    const exp = new Map<number, number>();
    for (const d of weekStarts) {
      const k = d.getTime();
      rev.set(k, 0);
      exp.set(k, 0);
    }

    for (const c of contracts) {

      const dt = new Date(c.date);
      if (dt.getFullYear() !== year) continue; // safety

      const weekKey = this._startOfISOWeek(dt).getTime();
      if (!rev.has(weekKey)) continue; // guard

      rev.set(weekKey, (rev.get(weekKey) ?? 0) + (c.revenue || 0));
      exp.set(weekKey, (exp.get(weekKey) ?? 0) + (c.expense || 0));
    }

    const revenueData = weekStarts.map((d) => ({ x: d, y: rev.get(d.getTime()) ?? 0 }));
    const expenseData = weekStarts.map((d) => ({ x: d, y: exp.get(d.getTime()) ?? 0 }));

    return [
      { name: this.transloco.translate('Revenue'), data: revenueData },
      { name: this.transloco.translate('Expenses'), data: expenseData },
    ];
  }

  // === ISO week helpers (Mon–Sun, week-year aware) ===
  private _startOfISOWeek(date: Date): Date {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const day = d.getUTCDay() || 7;           // Mon=1..Sun=7
    d.setUTCDate(d.getUTCDate() - (day - 1)); // back to Monday
    d.setUTCHours(0, 0, 0, 0);
    return d;
  }
  private _startOfFirstISOWeek(year: number): Date {
    const jan4 = new Date(Date.UTC(year, 0, 4));
    return this._startOfISOWeek(jan4);
  }
  private _isoWeekYear(date: Date): number {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const day = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - day);   // Thursday
    return d.getUTCFullYear();
  }
  private _allISOWeekStartsOfYear(year: number): Date[] {
    const weeks: Date[] = [];
    let d = this._startOfFirstISOWeek(year);
    while (this._isoWeekYear(d) === year) {
      weeks.push(new Date(d));
      d = new Date(d.getTime());
      d.setUTCDate(d.getUTCDate() + 7);
    }
    return weeks;
  }


  private _initializeData() {
    this.chartCompleted = {
      chart: {
        animations: {
          enabled: false,
        },
        fontFamily: 'inherit',
        foreColor: 'inherit',
        height: '100%',
        type: 'area',
        sparkline: {
          enabled: true,
        },
      },
      colors: ['#FB7185'],
      fill: {
        colors: ['#FB7185'],
        opacity: 0.5,
      },
      series: [0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0],
      stroke: {
        curve: 'smooth',
      },
      tooltip: {
        followCursor: true,
        theme: 'dark',
      },
      xaxis: {
        type: 'category',
        categories: this.months,
      },
      yaxis: {
        labels: {
          formatter: (val): string => val.toString(),
        },
      },
    };
    // Impressions
    this.chartDelivered = {
      chart: {
        animations: {
          enabled: false,
        },
        fontFamily: 'inherit',
        foreColor: 'inherit',
        height: '100%',
        type: 'area',
        sparkline: {
          enabled: true,
        },
      },
      colors: ['#34D399'],
      fill: {
        colors: ['#34D399'],
        opacity: 0.5,
      },
      series: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      stroke: {
        curve: 'smooth',
      },
      tooltip: {
        followCursor: true,
        theme: 'dark',
      },
      xaxis: {
        type: 'category',
        categories: this.months,
      },
      yaxis: {
        labels: {
          formatter: (val): string => val.toString(),
        },
      },
    };
    // Conversions
    this.chartAccounted = {
      chart: {
        animations: {
          enabled: false,
        },
        fontFamily: 'inherit',
        foreColor: 'inherit',
        height: '100%',
        type: 'area',
        sparkline: {
          enabled: true,
        },
      },
      colors: ['#38BDF8'],
      fill: {
        colors: ['#38BDF8'],
        opacity: 0.5,
      },
      series: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      stroke: {
        curve: 'smooth',
      },
      tooltip: {
        followCursor: true,
        theme: 'dark',
      },
      xaxis: {
        type: 'category',
        categories: this.months,
      },
      yaxis: {
        labels: {
          formatter: (val): string => val.toString(),
        },
      },
    };

    this.chartSummary = {
      chart: {
        animations: {
          speed: 400,
          animateGradually: {
            enabled: false,
          },
        },
        fontFamily: 'inherit',
        foreColor: 'inherit',
        width: '100%',
        height: '100%',
        type: 'area',
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
      },
      colors: ['#818CF8'],
      dataLabels: {
        enabled: false,
      },
      fill: {
        colors: ['#312E81'],
      },
      grid: {
        show: true,
        borderColor: '#334155',
        padding: {
          top: 10,
          bottom: -40,
          left: 0,
          right: 0,
        },
        position: 'back',
        xaxis: {
          lines: {
            show: true,
          },
        },
      },
      series: this.buildWeeklyRevenueExpenseSeriesCurrentYear(
        this.data.contracts
      ),
      stroke: {
        width: 2,
      },
      tooltip: {
        followCursor: true,
        theme: 'dark',
        x: {
          format: 'MMM dd, yyyy',
        },
        y: {
          formatter: (value: number): string => `${value}`,
        },
      },
      xaxis: {
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        crosshairs: {
          stroke: {
            color: '#475569',
            dashArray: 0,
            width: 2,
          },
        },
        labels: {
          offsetY: -20,
          style: {
            colors: '#CBD5E1',
          },
        },
        tickAmount: 20,
        tooltip: {
          enabled: false,
        },
        type: 'datetime',
      },
      yaxis: {
        axisTicks: {
          show: false,
        },
        axisBorder: {
          show: false,
        },
        min: (min): number => min - 750,
        max: (max): number => max + 250,
        tickAmount: 5,
        show: false,
      },
    };
  }
}